import { Modal, type ModalAction } from '@crm/ui';
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  CalendarCheck,
  Check,
  CircleCheck,
  Home,
  Send,
  X,
} from 'lucide-react';
import { useLandingConfig } from '@/modules/landing/api';
import { useAppointmentAction } from '../api';
import { useBookingMode } from '../booking-mode';
import { AppointmentWizard } from './wizard/appointment-wizard';
import { useAppointmentWizard } from './wizard/use-appointment-wizard';
import { whatsappInvite } from './wizard/whatsapp-link';

interface AppointmentDialogProps {
  /** Sem id, e um agendamento novo. */
  id?: string;
  /** Unidade escolhida na agenda - o wizard nasce com ela. */
  unitId?: string;
  /** Nome da unidade quando nao ha sessao para consultar (pagina publica). */
  unitName?: string;
  /**
   * `link` e o mesmo agendamento, mas pensado para o cliente terminar: o botao
   * de mandar no WhatsApp aparece assim que o cliente e confirmado.
   */
  intent?: 'appointment' | 'link';
  /** Abre direto no calendario: e o que "Reagendar" precisa. */
  reschedule?: boolean;
  /** Horario clicado na agenda - a escolha do slot ja abre nele. */
  startAt?: Date;
  onClose: () => void;
}

/**
 * Modal do agendamento em formato de wizard. As acoes ficam no rodape, como
 * dados, e mudam conforme o passo - o wizard e quem sabe o que vem depois.
 * Monte-a apenas quando aberta: cada abertura comeca com o estado limpo.
 *
 * Os rotulos sao curtos de proposito: dentro da modal do agendamento nao ha
 * o que "Salvar" ou "Cancelar" possa significar alem do proprio agendamento.
 */
export function AppointmentDialog({
  id,
  unitId,
  unitName,
  intent = 'appointment',
  reschedule = false,
  startAt,
  onClose,
}: AppointmentDialogProps) {
  const mode = useBookingMode();
  const wizard = useAppointmentWizard({
    id,
    unitId,
    unitName,
    startAt,
    initialStep: reschedule ? 'professional' : undefined,
    onDone: onClose,
  });
  const action = useAppointmentAction();
  // Na pagina publica nao ha sessao para ler a config; o slug ja vem do modo.
  const config = useLandingConfig({ enabled: mode === null });
  const slug = mode?.slug ?? config.data?.slug ?? null;

  /** Convite pronto para mandar: leva o id, entao o cliente continua de onde paramos. */
  const invite =
    slug && !mode
      ? whatsappInvite({
          slug,
          appointmentId: wizard.savedId,
          phone: wizard.customer?.phone ?? null,
          customerName: wizard.customer?.name ?? '',
        })
      : null;

  const sendToWhatsApp: ModalAction = {
    label: 'WhatsApp',
    // Icone de envio: a acao aqui e mandar o convite, nao abrir uma conversa.
    icon: <Send />,
    variant: 'outline',
    onClick: () => {
      if (invite) window.open(invite, '_blank', 'noopener');
    },
  };

  const actions: ModalAction[] = [];
  const back = (step: 'customer' | 'service'): ModalAction => ({
    label: 'Voltar',
    icon: <ArrowLeft />,
    variant: 'outline',
    onClick: () => wizard.goTo(step),
  });

  if (wizard.isConfirmed) {
    // Fim do fluxo publico: sair da modal ou mandar o resumo para alguem.
    actions.push(
      { label: 'Voltar ao inicio', icon: <Home />, variant: 'outline', onClick: onClose },
      {
        label: 'Enviar por WhatsApp',
        icon: <Send />,
        onClick: () =>
          window.open(
            `https://wa.me/?text=${encodeURIComponent(wizard.summaryText)}`,
            '_blank',
            'noopener',
          ),
      },
    );
  } else if (!wizard.editable) {
    // Atendimento fechado nao tem o que salvar: o X do cabecalho ja fecha, e
    // um rodape so com "Fechar" repetiria o mesmo botao.
  } else if (wizard.step === 'customer') {
    actions.push({ label: 'Cancelar', icon: <X />, variant: 'ghost', onClick: onClose });

    // No link, mandar o convite ja vale assim que o cliente esta confirmado -
    // sem travar quem quiser adiantar servico e horario.
    if (intent === 'link' && wizard.customer && invite) actions.push(sendToWhatsApp);

    actions.push({
      label: 'Avancar',
      icon: <ArrowRight />,
      disabled: wizard.customer === null,
      onClick: () => wizard.goTo('service'),
    });
  } else if (wizard.step === 'service') {
    actions.push(back('customer'), {
      label: 'Avancar',
      icon: <ArrowRight />,
      disabled: wizard.rows.length === 0,
      onClick: () => wizard.goTo('summary'),
    });
  } else if (wizard.step === 'professional') {
    // "Confirmar" e do ultimo passo: aqui ainda se esta escolhendo, e o
    // rotulo precisa dizer que o fluxo continua.
    actions.push(back('service'), {
      label: 'Avancar',
      icon: <ArrowRight />,
      disabled: wizard.choice === null,
      onClick: wizard.confirmSlot,
    });
  } else {
    actions.push(back('service'));

    // No resumo de um agendamento existente cabem tambem as acoes de status -
    // que sao da operacao, nao do cliente que agenda pela pagina publica.
    if (!mode && id && wizard.appointment?.status === 'SCHEDULED') {
      actions.push({
        label: 'Confirmar',
        icon: <CircleCheck />,
        variant: 'outline',
        disabled: action.isPending,
        onClick: () => action.mutate({ id, action: 'confirm' }),
      });
    }

    if (!mode && id && wizard.appointment) {
      actions.push({
        label: 'Cancelar',
        icon: <Ban />,
        variant: 'outline',
        disabled: action.isPending,
        onClick: () => {
          const reason = window.prompt('Motivo do cancelamento (opcional):') ?? undefined;
          action.mutate({ id, action: 'cancel', reason });
        },
      });
    }

    if (invite && wizard.customer) actions.push(sendToWhatsApp);

    // Na pagina publica a acao e sempre agendar - o autosave e detalhe de
    // implementacao, nao algo que o cliente precise entender.
    const isSaving = !mode && Boolean(id || wizard.savedId);

    // "Finalizar" e o unico ponto em que o cliente e avisado: ate aqui a
    // comanda ainda pode ganhar servico, mesmo com o horario ja gravado.
    actions.push({
      label: isSaving ? 'Finalizar' : 'Agendar',
      icon: isSaving ? <Check /> : <CalendarCheck />,
      loading: wizard.isSubmitting,
      onClick: wizard.submit,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={
        wizard.isConfirmed
          ? 'Tudo certo'
          : mode
            ? 'Agendar horario'
            : id
              ? 'Agendamento'
              : 'Novo agendamento'
      }
      subtitle={wizard.unitName}
      size="full"
      // Fundo cinza atras dos cartoes: sobre o branco da modal, os blocos do
      // resumo nao se distinguiam do vazio.
      bodyClassName="bg-content"
      actions={actions}
    >
      <AppointmentWizard wizard={wizard} />
    </Modal>
  );
}
